<template>
  <modal-wrap
    :id="id"
    ref="IntegrationsOngoingSettings"
    :name="modalName"
    :header-class="'integrations-ongoing-settings-header'"
    body-class="integrations-ongoing-settings-modal"
    :hide-footer="true"
    size="md"
  >
    <template #modal-title>
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0">OnGoing Webhook Settings</h4>
      </div>
    </template>
    <template #default>
      <b-form-group>
        <div class="form-group">
          <div class="d-flex justify-content-between align-items-center">
            <h4 class="mb-0">Webhook URL</h4>
          </div>

          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                1. Administration -> Integration -> Webhooks -> Create new automation webhook
                <br /><br />
                2. Choose webhook type (Order Picked or Order created) and paste the following URL:
                <br />
                {{ importUrl }}/webhook/shopify?client_internal_id={{ customerId }} <br /><br />
                3. Administration -> Goods owners and users -> Users -> Create new user
                <br />
                You need to create user with Access Rights "Automation API user" and paste credentials below.
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center">
            <h4 class="mb-0">Connection Settings</h4>
          </div>

          <div class="row">
            <div class="form-group mb-0">
              <div class="form-check mb-0 mt-0">
                <input id="sandbox" v-model="sandbox" class="form-check-input" type="checkbox" :value="true" />
                <label for="sandbox" class="form-check-label mb-0"> Sandbox </label>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label>Warehouse</label>
                <VInput
                  v-model="warehouseId"
                  type="text"
                  name="customerId"
                  placeholder="Warehouse ID"
                  :rules="{ required: true, min: 3 }"
                />
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label>OnGoing User</label>
                <VInput
                  v-model="user"
                  type="text"
                  name="user"
                  placeholder="Login"
                  :rules="{ required: true, min: 3 }"
                />
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label>OnGoing Password</label>
                <VInput
                  v-model="pwd"
                  type="password"
                  name="pwd"
                  placeholder="Password"
                  :rules="{ required: true, min: 8 }"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="form-group">
          <button type="button" class="btn create-btn btn-green" @click="connect">Connect</button>
        </div>
      </b-form-group>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { OnGoingIntegrationModal } from '@/types/Models/Integration'
import { notify } from '@kyvg/vue3-notification'
import { BFormGroup } from 'bootstrap-vue-next'
import { defineComponent, ref, computed, watch, type ComputedRef, type Ref, toRaw } from 'vue'
import { ONGOING } from '@/const/IntegrationTypes'
import { ONGOING_WEBHOOK_SETTINGS } from '@/const/ModalTypes'
import { useCustomersIntegrationsStore } from '@/store/customers/integrations'
import { useVueNextModalStore } from '@/store/vueNextModal'
import VInput from '@/views/Components/Elements/Form/VInput.vue'
import ModalWrap from '@/views/Modals/ModalWrap.vue'

export default defineComponent({
  name: 'IntegrationsOngoingSettingsModal',
  components: {
    BFormGroup,
    ModalWrap,
    VInput,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup() {
    const modalStore = useVueNextModalStore()
    const customersIntegrationsStore = useCustomersIntegrationsStore()

    const importUrl: string = import.meta.env.VITE_APP_INTEGRATION_URL || ''
    const warehouseId: Ref<string> = ref('')
    const sandbox: Ref<boolean> = ref(false)
    const user: Ref<string> = ref('')
    const pwd: Ref<string> = ref('')

    const modalName = ONGOING_WEBHOOK_SETTINGS
    const modalData: ComputedRef<OnGoingIntegrationModal | null> = computed(
      () => modalStore.modalPropsByName(modalName) || null,
    )
    const customerId: ComputedRef<string> = computed(() => modalData.value?.customerId || '')

    const connect = () => {
      if (!modalData.value) return
      customersIntegrationsStore
        .connect({
          customerId: customerId.value,
          data: {
            marketplace: ONGOING,
            import_interval: 1,
            import_type: 'on_demand',
            active: true,
            default_weight: 500,
            default_length: 34,
            default_width: 24,
            default_height: 7,
            credentials: {
              key: user.value,
              secret: pwd.value,
              sandbox: sandbox.value,
              warehouse_id: warehouseId.value,
            },
          },
        })
        .then((response) => {
          if (response.message) {
            notify({ group: 'error', text: response.message })
          } else {
            notify({ group: 'success', text: 'The integration has been connected.' })
            customersIntegrationsStore.fetch(toRaw(customerId.value))
            modalStore.closeModal(modalName)
          }
        })
    }

    watch(modalData, () => {
      if (!modalData.value) return
      warehouseId.value = modalData.value.warehouseId
      sandbox.value = modalData.value.sandbox
      user.value = modalData.value.key || ''
      pwd.value = modalData.value.secret
    })

    return {
      connect,
      importUrl,
      modalName,
      modalData,
      warehouseId,
      customerId,
      sandbox,
      user,
      pwd,
    }
  },
})
</script>
